import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Building2, Mail, Pencil, Plus, RotateCcw, ShieldCheck, Trash2 } from 'lucide-react';
import { formatDate, formatMoney } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, SearchInput, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/use-confirm';
import { CountrySelect, PostalCodeField, ProvinceSelect } from '@/components/ui/geo-fields';
import { CurrencyField } from '@/components/ui/currency-field';
import { useEditLock } from '@/hooks/useEditLock';
import { EditLockBanner } from '@/components/ui/edit-lock-banner';

interface SupplierRow {
  id: string; code: string; name: string; vat_number: string | null; email: string | null;
  phone: string | null; city: string | null; address: string | null; postal_code: string | null;
  country: string; payment_terms: string | null; is_provisional: boolean; is_trusted: boolean;
  country_code: string | null; region_code: string | null;
  payment_method_id: string | null; iban: string | null; lead_time_days: number | null; currency: string;
  is_active: boolean;
  notes: string | null; product_count: number; invoice_count: number;
}

interface SupplierDetail extends SupplierRow {
  products: { id: string; product_id: string; supplier_sku: string | null; last_cost: number; sku: string; name: string; unit_code: string }[];
  invoices: { id: string; invoice_number: string; invoice_date: string; total: number; status: string }[];
  invoice_template: {
    version?: number; createdAt?: string; source?: string;
    columns?: Record<string, unknown>;
    anchors?: Record<string, { text?: string }>;
  } | null;
  invoice_templates: {
    id: string; name: string; isDefault: boolean; usageCount: number;
    createdAt?: string;
    config: {
      version?: number; columns?: Record<string, unknown>;
      anchors?: Record<string, { text?: string }>;
      pages?: Record<string, unknown>;
    };
  }[];
}

const EMPTY = {
  code: '', name: '', vatNumber: '', email: '', phone: '', address: '', city: '', postalCode: '',
  country: 'ES', countryCode: 'ES', regionCode: '',
  paymentTerms: '30 días', paymentMethodId: '', iban: '', notes: '', isTrusted: false, isProvisional: false,
  leadTimeDays: '', currency: 'EUR'
};

export function SuppliersPage() {
  const { user, can } = useAuth();
  const isAdmin = Boolean(user?.roles.some((role) => role.code === 'admin'));
  const qc = useQueryClient();
  const { confirm, dialog: dialogConfirmacion } = useConfirm();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  // Los archivados se ven aparte: siguen ahí por su historial, pero no deben mezclarse con los activos.
  const [vista, setVista] = useState<'activos' | 'archivados'>('activos');
  const [page, setPage] = useState(1);
  // Cambiar de vista (activos/archivados) vuelve a la primera página: si no, con pocos archivados la
  // tabla salía vacía y el paginador desaparecía sin forma de volver.
  const cambiarVista = (nueva: 'activos' | 'archivados') => { setVista(nueva); setPage(1); };

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', search, vista, page],
    // Paginado: antes se cortaba en 200 sin decirlo.
    queryFn: () => api<{ data: SupplierRow[]; meta: { page: number; pageSize: number; total: number; totalPages: number } }>(
      `/suppliers?active=${vista === 'archivados' ? 'false' : 'true'}&page=${page}${search ? `&q=${encodeURIComponent(search)}` : ''}`),
    placeholderData: keepPreviousData
  });
  const metaSuppliers = suppliersQuery.data?.meta;

  // Si al archivar (o borrar) el último de la página se queda vacía, se vuelve a la anterior: si no, la
  // pantalla muestra «0» y el paginador desaparece sin forma de salir.
  useEffect(() => {
    if (suppliersQuery.isSuccess && !suppliersQuery.isFetching && page > 1 && (suppliersQuery.data?.data.length ?? 0) === 0) setPage(page - 1);
  }, [suppliersQuery.isSuccess, suppliersQuery.isFetching, suppliersQuery.data, page]);

  const reactivarSupplier = async (supplier: { id: string; name: string }) => {
    try {
      await api(`/suppliers/${supplier.id}`, { method: 'PATCH', body: { isActive: true } });
      toast.success('Proveedor reactivado');
      await refreshAfterDelete();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const detailQuery = useQuery({
    queryKey: ['supplier', detailId],
    queryFn: () => api<SupplierDetail>(`/suppliers/${detailId}`),
    enabled: Boolean(detailId)
  });

  const rows = suppliersQuery.data?.data ?? [];


  const refreshAfterDelete = async () => {
    setDetailId(null);
    await qc.invalidateQueries({ queryKey: ['suppliers'] });
    await qc.invalidateQueries({ queryKey: ['supplier'] });
    await qc.invalidateQueries({ queryKey: ['invoices'] });
  };

  const archiveSupplier = async (supplier: { id: string; name: string }) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Archivar ${supplier.name}`,
      description: 'Deja de aparecer en los listados y selectores; sus facturas y pedidos históricos se conservan.',
      confirmLabel: 'Archivar'
    }))) return;
    try {
      await api(`/suppliers/${supplier.id}`, { method: 'DELETE' });
      toast.success('Proveedor archivado');
      await refreshAfterDelete();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const purgeSupplier = async (supplier: { id: string; name: string }) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Eliminar definitivamente ${supplier.name}`,
      description: 'No se puede deshacer. Solo se permite si no tiene facturas, pedidos, documentos ni artículos vinculados.',
      confirmLabel: 'Eliminar definitivamente', destructive: true
    }))) return;
    try {
      await api(`/suppliers/${supplier.id}?permanent=true`, { method: 'DELETE' });
      toast.success('Proveedor eliminado definitivamente');
      await refreshAfterDelete();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const removeInvoiceTemplate = async (supplier: { id: string; name: string }, templateId: string, templateName: string) => {
    if (!(await confirm({
      title: `Eliminar la plantilla ${templateName}`,
      description: `De ${supplier.name}. Las próximas facturas usarán otra plantilla o el parser genérico.`,
      confirmLabel: 'Eliminar plantilla', destructive: true
    }))) return;
    try {
      await api(`/suppliers/${supplier.id}/invoice-templates/${templateId}`, { method: 'DELETE' });
      toast.success('Plantilla de factura eliminada');
      await qc.invalidateQueries({ queryKey: ['supplier'] });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const setDefaultInvoiceTemplate = async (supplier: { id: string }, templateId: string) => {
    try {
      await api(`/suppliers/${supplier.id}/invoice-templates/${templateId}`, { method: 'PUT', body: { isDefault: true } });
      toast.success('Plantilla predeterminada actualizada');
      await qc.invalidateQueries({ queryKey: ['supplier'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const columns: Column<SupplierRow>[] = [
    {
      key: 'name',
      header: 'Proveedor',
      cell: (row) => (
        <div className="min-w-[220px]">
          <p className="font-medium">{row.name}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{row.code}{row.vat_number ? ` · ${row.vat_number}` : ''}</p>
        </div>
      )
    },
    { key: 'contact', header: 'Contacto', cell: (row) => <div className="text-[11px] text-muted-foreground"><p>{row.email ?? '—'}</p><p>{row.phone ?? ''}</p></div> },
    { key: 'city', header: 'Ciudad', cell: (row) => <span className="text-muted-foreground">{row.city ?? '—'}</span> },
    { key: 'products', header: 'Artículos', cell: (row) => <span className="tabular-nums">{row.product_count}</span> },
    { key: 'invoices', header: 'Facturas', cell: (row) => <span className="tabular-nums">{row.invoice_count}</span> },
    {
      key: 'status',
      header: 'Estado',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.is_trusted ? <Badge tone="success"><ShieldCheck className="size-3" /> Confiable</Badge> : null}
          {row.is_provisional ? <Badge tone="warning">Provisional</Badge> : null}
          {!row.is_trusted && !row.is_provisional ? <Badge tone="neutral">Estándar</Badge> : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => {
        const acciones: RowAction[] = [
          { key: 'edit', label: 'Editar', icon: <Pencil />, onClick: () => { setEditing(row); setFormOpen(true); } }
        ];
        // En la pestaña de archivados siempre se ofrece Reactivar, aunque el campo no llegue.
        if (isAdmin && (row.is_active === false || vista === 'archivados')) {
          acciones.push({ key: 'restore', label: 'Reactivar', icon: <RotateCcw />, onClick: () => void reactivarSupplier(row) });
        } else if (isAdmin) {
          acciones.push({ key: 'archive', label: 'Archivar (conserva historial)', icon: <Archive />, onClick: () => void archiveSupplier(row) });
        }
        if (isAdmin) {
          acciones.push({ key: 'purge', label: 'Eliminar definitivamente (solo sin historial)', icon: <Trash2 />, destructive: true, onClick: () => void purgeSupplier(row) });
        }
        return <RowActions actions={acciones} />;
      }
    }
  ];

  return (
    <div>
      {dialogConfirmacion}
      <div className={formOpen || detailId ? 'hidden' : ''}>
      <PageHeader
        title="Proveedores"
        description="Maestro de proveedores, referencias de compra, costes y fiabilidad"
        actions={(
          <div className="flex items-center gap-2">
            <Button variant={vista === 'activos' ? 'default' : 'outline'} size="sm"
              onClick={() => cambiarVista('activos')}>Activos</Button>
            <Button variant={vista === 'archivados' ? 'default' : 'outline'} size="sm"
              onClick={() => cambiarVista('archivados')}>Archivados</Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }}><Plus /> Nuevo proveedor</Button>
          </div>
        )}
      />
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={(valor) => { setSearch(valor); setPage(1); }} placeholder="Buscar por nombre, CIF o código…" className="w-full sm:w-80" />
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground"><Building2 className="size-3" /> {metaSuppliers?.total ?? rows.length} proveedores</div>
        </div>
        {suppliersQuery.error ? <ErrorState message={(suppliersQuery.error as Error).message} onRetry={() => void suppliersQuery.refetch()} /> : (
          <DataTable
            columns={columns} rows={rows} loading={suppliersQuery.isLoading} rowKey={(row) => row.id}
            onRowClick={(row) => setDetailId(row.id)}
            empty={<EmptyState
              title={vista === 'archivados' ? 'Sin proveedores archivados' : 'Sin proveedores'}
              description={vista === 'archivados'
                ? 'Aquí aparecen los que has archivado, con su historial.'
                : 'Crea un proveedor para empezar a recibir facturas.'}
              icon={Building2} />}
          />
        )}
        {metaSuppliers && metaSuppliers.totalPages > 1 ? (
          <Pagination page={metaSuppliers.page} totalPages={metaSuppliers.totalPages}
            total={metaSuppliers.total} onPageChange={setPage} />
        ) : null}
      </Card>
      </div>

      <SupplierFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        supplier={editing}
        onSaved={() => { void qc.invalidateQueries({ queryKey: ['suppliers'] }); setFormOpen(false); setEditing(null); }}
      />

      <InlineView
        open={Boolean(detailId)}
        onOpenChange={(open) => { if (!open) setDetailId(null); }}
        title={detailQuery.data?.name ?? 'Proveedor'}
        description={detailQuery.data ? `${detailQuery.data.code} · CIF ${detailQuery.data.vat_number ?? '—'}` : undefined}
        size="lg"
        footer={isAdmin && detailQuery.data ? (
          <>
            {detailQuery.data.is_active === false ? (
              <Button variant="outline" onClick={() => void reactivarSupplier(detailQuery.data!)}><RotateCcw /> Reactivar</Button>
            ) : (
              <Button variant="outline" onClick={() => void archiveSupplier(detailQuery.data!)}><Archive /> Archivar</Button>
            )}
            <Button variant="destructive" onClick={() => void purgeSupplier(detailQuery.data!)}><Trash2 /> Eliminar definitivamente</Button>
          </>
        ) : null}
      >
        {detailQuery.isLoading || !detailQuery.data ? <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}</div> : (
          <div className="space-y-5">
            <div className="grid gap-3 rounded-lg border border-border bg-muted/20 p-3 text-xs sm:grid-cols-3">
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Contacto</p><p className="mt-1">{detailQuery.data.email ?? '—'}</p><p>{detailQuery.data.phone ?? ''}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Dirección</p><p className="mt-1">{detailQuery.data.address ?? '—'}</p><p>{detailQuery.data.postal_code} {detailQuery.data.city}</p></div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pago</p>
                <p className="mt-1">{detailQuery.data.payment_terms ?? '—'}</p>
                <p className="text-muted-foreground">Moneda: {detailQuery.data.currency ?? 'EUR'}</p>
              </div>
            </div>
            <div className="rounded-lg border border-border p-3 text-xs">
              <p className="font-semibold">Plantillas de factura PDF ({detailQuery.data.invoice_templates.length})</p>
              {detailQuery.data.invoice_templates.length === 0 ? (
                <p className="mt-1 text-muted-foreground">No configuradas. Se genera una automáticamente al importar una factura PDF; desde la factura puedes crear plantillas visuales con diccionario.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {detailQuery.data.invoice_templates.map((template) => (
                    <div key={template.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border bg-muted/20 p-2">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium">
                          {template.name}
                          {template.isDefault ? <Badge tone="success">Predeterminada</Badge> : null}
                        </p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {Object.keys(template.config?.columns ?? {}).length} columnas · {Object.keys(template.config?.anchors ?? {}).length} anclas
                          {template.config?.pages ? ` · ${Object.keys(template.config.pages).length} páginas` : ''}
                          {template.createdAt ? ` · ${formatDate(template.createdAt)}` : ''}
                        </p>
                        {template.config?.anchors ? (
                          <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                            Diccionario: {Object.values(template.config.anchors).map((anchor) => anchor?.text).filter(Boolean).join(' · ')}
                          </p>
                        ) : null}
                      </div>
                      {can('suppliers.write') ? (
                        <div className="flex gap-1">
                          {!template.isDefault ? <Button variant="outline" size="sm" onClick={() => void setDefaultInvoiceTemplate(detailQuery.data!, template.id)}>Predeterminar</Button> : null}
                          <Button variant="outline" size="sm" onClick={() => void removeInvoiceTemplate(detailQuery.data!, template.id, template.name)}>Eliminar</Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold">Artículos suministrados</p>
              <table className="table-dense w-full rounded border border-border">
                <thead className="bg-muted/30"><tr><th>Artículo</th><th>Ref. proveedor</th><th className="text-right">Último coste</th></tr></thead>
                <tbody>
                  {detailQuery.data.products.length === 0 ? <tr><td colSpan={3} className="text-center text-muted-foreground">Sin artículos vinculados</td></tr> : detailQuery.data.products.map((product) => (
                    <tr key={product.id}><td>{product.name} <span className="font-mono text-[10px] text-muted-foreground">{product.sku}</span></td><td className="font-mono text-[11px]">{product.supplier_sku ?? '—'}</td><td className="text-right">{formatMoney(product.last_cost)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold">Últimas facturas</p>
              <table className="table-dense w-full rounded border border-border">
                <thead className="bg-muted/30"><tr><th>Número</th><th>Fecha</th><th>Estado</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {detailQuery.data.invoices.length === 0 ? <tr><td colSpan={4} className="text-center text-muted-foreground">Sin facturas</td></tr> : detailQuery.data.invoices.map((invoice) => (
                    <tr key={invoice.id}><td className="font-mono text-[11px]">{invoice.invoice_number}</td><td>{formatDate(invoice.invoice_date)}</td><td><StatusBadge status={invoice.status} /></td><td className="text-right">{formatMoney(invoice.total)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </InlineView>
    </div>
  );
}

function SupplierFormModal({ open, onOpenChange, supplier, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; supplier: SupplierRow | null; onSaved: () => void;
}) {
  const [form, setForm] = useState({ ...EMPTY });
  // Un proveedor nuevo no existe todavía: no hay nada que bloquear hasta que se guarde.
  const lock = useEditLock('supplier', open && supplier ? supplier.id : null);
  const soloLectura = Boolean(supplier) && !lock.canEdit;
  // Formas de pago de proveedor: solo las de pago (o las que valen para todo).
  const formasQuery = useQuery({
    queryKey: ['payment-methods', 'pagos'],
    queryFn: () => api<{ data: { id: string; name: string }[] }>('/payment-methods?scope=pagos'),
    staleTime: 10 * 60 * 1000
  });
  const formasPago = formasQuery.data?.data ?? [];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (supplier) {
      setForm({
        code: supplier.code, name: supplier.name, vatNumber: supplier.vat_number ?? '',
        email: supplier.email ?? '', phone: supplier.phone ?? '', address: supplier.address ?? '',
        city: supplier.city ?? '', postalCode: supplier.postal_code ?? '', country: supplier.country,
        countryCode: supplier.country_code ?? '', regionCode: supplier.region_code ?? '',
        paymentTerms: supplier.payment_terms ?? '',
        currency: supplier.currency ?? 'EUR',
        leadTimeDays: supplier.lead_time_days === null || supplier.lead_time_days === undefined
          ? '' : String(supplier.lead_time_days),
        paymentMethodId: (supplier as SupplierRow & { payment_method_id?: string | null }).payment_method_id ?? '',
        iban: (supplier as SupplierRow & { iban?: string | null }).iban ?? '',
        notes: supplier.notes ?? '',
        isTrusted: supplier.is_trusted, isProvisional: supplier.is_provisional
      });
    } else {
      setForm({ ...EMPTY });
    }
  }, [open, supplier]);

  const update = (key: keyof typeof form, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!form.name.trim()) throw new Error('El nombre es obligatorio');
      // El plazo va a la fecha del pedido: tiene que ser un entero de días, no «1,5» ni «-3».
      if (form.leadTimeDays !== '' && !/^\d{1,3}$/.test(form.leadTimeDays.trim())) {
        throw new Error('El plazo de entrega son días enteros (0 a 365)');
      }
      if (form.leadTimeDays !== '' && (Number(form.leadTimeDays) < 0 || Number(form.leadTimeDays) > 365)) {
        throw new Error('El plazo de entrega tiene que estar entre 0 y 365 días');
      }
      const payload = {
        code: form.code || undefined, name: form.name, vatNumber: form.vatNumber || null,
        email: form.email || null, phone: form.phone || null, address: form.address || null,
        city: form.city || null, postalCode: form.postalCode || null, country: form.country || 'ES',
        countryCode: form.countryCode || null, regionCode: form.regionCode || null,
        paymentTerms: form.paymentTerms || null,
        paymentMethodId: form.paymentMethodId || null, iban: form.iban || null,
        // Días que se tardan en servir: con esto el pedido propone su fecha prevista.
        leadTimeDays: form.leadTimeDays === '' ? null : Number(form.leadTimeDays),
        currency: form.currency || 'EUR',
        notes: form.notes || null,
        isTrusted: form.isTrusted, isProvisional: form.isProvisional
      };
      if (supplier) await api(`/suppliers/${supplier.id}`, { method: 'PATCH', body: payload });
      else await api('/suppliers', { method: 'POST', body: payload });
      toast.success('Proveedor guardado');
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="lg"
      title={supplier ? `Editar ${supplier.name}` : 'Nuevo proveedor'}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} disabled={soloLectura} onClick={() => void submit()}>Guardar</Button></>}
    >
      <div className="space-y-4">
        {/* Fuera del fieldset: sus botones tienen que seguir activos en solo lectura. */}
        <EditLockBanner
          state={lock.state}
          onTakeOver={() => void lock.takeOver()}
          onRetry={() => void lock.retry()}
        />
        <fieldset disabled={soloLectura} className="m-0 min-w-0 space-y-4 border-0 p-0">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Código" hint="Automático si se deja vacío"><Input value={form.code} onChange={(e) => update('code', e.target.value)} disabled={Boolean(supplier)} /></Field>
          <Field label="Nombre" required className="sm:col-span-2"><Input value={form.name} onChange={(e) => update('name', e.target.value)} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="CIF / VAT"><Input value={form.vatNumber} onChange={(e) => update('vatNumber', e.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} /></Field>
          <Field label="Teléfono"><Input value={form.phone} onChange={(e) => update('phone', e.target.value)} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Dirección" className="sm:col-span-2"><Input value={form.address} onChange={(e) => update('address', e.target.value)} /></Field>
          <Field label="Ciudad"><Input value={form.city} onChange={(e) => update('city', e.target.value)} /></Field>
          <Field label="C.P.">
            <PostalCodeField
              countryCode={form.countryCode} value={form.postalCode} city={form.city}
              onChange={(texto) => update('postalCode', texto)}
              onApply={(cambios) => setForm((f) => ({
                ...f,
                ...(cambios.city !== undefined ? { city: cambios.city } : {}),
                ...(cambios.regionCode !== undefined ? { regionCode: cambios.regionCode } : {})
              }))}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Condiciones de pago" hint="Texto libre, para lo que diga el contrato"><Input value={form.paymentTerms} onChange={(e) => update('paymentTerms', e.target.value)} /></Field>
          <CurrencyField value={form.currency} onChange={(code) => update('currency', code)} />
          <Field label="Plazo de entrega (días)" hint="Lo que tarda en servir: con esto se propone la fecha del pedido">
            <Input type="number" min="0" max="365" value={form.leadTimeDays}
              onChange={(e) => update('leadTimeDays', e.target.value)} placeholder="p. ej. 10" />
          </Field>
          <Field label="Forma de pago" hint="Es la que se propone al pedir y al pagar">
            <Select value={form.paymentMethodId} onChange={(e) => update('paymentMethodId', e.target.value)}>
              <option value="">Sin definir</option>
              {/* Si la forma guardada ya no está activa, se pinta igual: si no, al guardar cualquier
                  cambio el proveedor se quedaría sin forma de pago sin que nadie lo pida. */}
              {form.paymentMethodId && !formasPago.some((forma) => forma.id === form.paymentMethodId) ? (
                <option value={form.paymentMethodId}>La asignada (inactiva)</option>
              ) : null}
              {formasPago.map((forma) => (
                <option key={forma.id} value={forma.id}>{forma.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="IBAN del proveedor" hint="Para pagarle por transferencia">
            <Input value={form.iban} onChange={(e) => update('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
          </Field>
          <Field label="País">
            <CountrySelect
              value={form.countryCode}
              onChange={(code) => setForm((f) => ({ ...f, countryCode: code, country: code, regionCode: '' }))}
            />
          </Field>
          <Field label="Provincia">
            <ProvinceSelect
              countryCode={form.countryCode} value={form.regionCode} allowText={false}
              onChange={(code) => setForm((f) => ({ ...f, regionCode: code }))}
            />
          </Field>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.isTrusted} onChange={(e) => update('isTrusted', e.target.checked)} /> Proveedor confiable</label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.isProvisional} onChange={(e) => update('isProvisional', e.target.checked)} /> Ficha provisional</label>
        </div>
        <Field label="Notas"><Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={2} /></Field>
        </fieldset>
      </div>
    </InlineView>
  );
}
